/**
 * Query Key Factory
 *
 * Centralized management of all query keys for consistent cache invalidation.
 * Following TanStack Query best practices for hierarchical key structure.
 */

export const queryKeys = {
  /**
   * Posts and Feed
   */
  posts: {
    all: ["posts"] as const,
    lists: () => [...queryKeys.posts.all, "list"] as const,
    detail: (id: string) => [...queryKeys.posts.all, "detail", id] as const,
    engagement: (id: string) =>
      [...queryKeys.posts.detail(id), "engagement"] as const,
    comments: (postId: string) =>
      [...queryKeys.posts.detail(postId), "comments"] as const,
  },

  /**
   * Feed queries
   */
  feed: {
    all: ["feed"] as const,
    forYou: () => [...queryKeys.feed.all, "for-you"] as const,
    following: () => [...queryKeys.feed.all, "following"] as const,
    trending: () => [...queryKeys.feed.all, "trending"] as const,
    music: () => [...queryKeys.feed.all, "music"] as const,
    allPosts: () => [...queryKeys.feed.all, "all-posts"] as const,
    timeline: (userId: string) =>
      [...queryKeys.feed.all, "timeline", userId] as const,
  },

  /**
   * Users
   */
  users: {
    all: ["users"] as const,
    profile: (username: string) =>
      [...queryKeys.users.all, "profile", username] as const,
    followers: (userId: string) =>
      [...queryKeys.users.all, "followers", userId] as const,
    following: (userId: string) =>
      [...queryKeys.users.all, "following", userId] as const,
  },

  /**
   * Profile queries
   */
  profile: {
    all: ["profile"] as const,
    user: (username: string) => [...queryKeys.profile.all, username] as const,
    posts: (username: string, searchType?: string) =>
      [
        ...queryKeys.profile.user(username),
        "posts",
        searchType ?? "all",
      ] as const,
    songs: (username: string) =>
      [...queryKeys.profile.user(username), "songs"] as const,
    likes: (username: string) =>
      [...queryKeys.profile.user(username), "likes"] as const,
  },

  /**
   * Notifications
   */
  notifications: {
    all: ["notifications"] as const,
    list: (type?: string) =>
      [...queryKeys.notifications.all, "list", type] as const,
    unreadCount: () =>
      [...queryKeys.notifications.all, "unread-count"] as const,
  },

  /**
   * Search
   */
  search: {
    all: ["search"] as const,
    posts: (query: string) =>
      [...queryKeys.search.all, "posts", query] as const,
    users: (query: string) =>
      [...queryKeys.search.all, "users", query] as const,
  },

  /**
   * Trending
   */
  trending: {
    all: ["trending"] as const,
    posts: () => [...queryKeys.trending.all, "posts"] as const,
    hashtags: () => [...queryKeys.trending.all, "hashtags"] as const,
  },

  /**
   * Messages/Chat
   */
  messages: {
    all: ["messages"] as const,
    groups: () => [...queryKeys.messages.all, "groups"] as const,
    conversation: (groupUlid: string) =>
      [...queryKeys.messages.all, "conversation", groupUlid] as const,
  },

  /**
   * Playlists
   */
  playlists: {
    all: ["playlists"] as const,
    list: (username: string) =>
      [...queryKeys.playlists.all, "list", username] as const,
    detail: (playlistId: string) =>
      [...queryKeys.playlists.all, "detail", playlistId] as const,
    public: ["playlists", "public"] as const,
  },

  /**
   * CEO Dashboard
   */
  dashboard: {
    all: ["dashboard"] as const,
    summary: (params?: {
      preset?: string;
      startDate?: string;
      endDate?: string;
    }) => [...queryKeys.dashboard.all, "summary", params] as const,
    timeseries: (params: {
      metric: string;
      preset?: string;
      startDate?: string;
      endDate?: string;
    }) => [...queryKeys.dashboard.all, "timeseries", params] as const,
    listeningDistribution: (params?: {
      preset?: string;
      startDate?: string;
      endDate?: string;
    }) =>
      [...queryKeys.dashboard.all, "listening-distribution", params] as const,
    hourlyActiveUsers: () =>
      [...queryKeys.dashboard.all, "hourly-active-users"] as const,
  },
} as const;

/**
 * Helper function to invalidate all queries related to a post
 */
export function invalidatePostQueries(
  queryClient: {
    invalidateQueries: (opts: { queryKey: readonly string[] }) => void;
  },
  postId: string,
): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.posts.detail(postId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.posts.lists() });
  queryClient.invalidateQueries({ queryKey: queryKeys.feed.all });
}

/**
 * Helper function to invalidate all queries related to a user
 */
export function invalidateUserQueries(
  queryClient: {
    invalidateQueries: (opts: { queryKey: readonly string[] }) => void;
  },
  username: string,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.users.profile(username),
  });
  queryClient.invalidateQueries({ queryKey: queryKeys.profile.user(username) });
}
